/**
 * Who is signed in, for the whole app.
 *
 * Asks `GET /auth/me` on load. A 401 from any API call later (session expired,
 * signed out on this device) flips the state to signed-out and remembers where
 * the learner was, so they come back there after signing in again.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiFetch, setUnauthorizedHandler } from '../api/client';
import { clearReturnTo, saveReturnTo } from './returnTo';

export interface SignedInUser {
  userId: string;
  email: string;
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-in'; user: SignedInUser }
  | { status: 'signed-out'; deliberate?: boolean }
  | { status: 'error' };

export interface AuthContextValue {
  state: AuthState;
  /** Re-ask the backend who is signed in (after verify, or "Try again"). */
  refresh: () => Promise<void>;
  /**
   * End this device's session. Resolves true once the server has ended it;
   * false if it couldn't (the learner stays signed in and can retry).
   */
  signOut: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  // Only the newest /auth/me answer counts: an earlier request (e.g. on load,
  // racing a sign-in link being verified) can come back later with a stale 401.
  const latestRequest = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    const request = ++latestRequest.current;
    const isStale = (): boolean => request !== latestRequest.current;
    try {
      const response = await apiFetch(
        '/auth/me',
        {},
        { notifyUnauthorized: false }
      );
      if (isStale()) return;
      if (response.ok) {
        const data = (await response.json()) as {
          user_id: string;
          email: string;
        };
        setState({
          status: 'signed-in',
          user: { userId: data.user_id, email: data.email },
        });
      } else if (response.status === 401) {
        setState({ status: 'signed-out' });
      } else {
        setState({ status: 'error' });
      }
    } catch {
      if (!isStale()) setState({ status: 'error' });
    }
  }, []);

  const signOut = useCallback(async (): Promise<boolean> => {
    let ended = false;
    try {
      const response = await apiFetch(
        '/auth/sign-out',
        { method: 'POST' },
        { notifyUnauthorized: false }
      );
      // 401: there was no live session to end, which is the outcome we wanted.
      ended = response.ok || response.status === 401;
    } catch {
      ended = false;
    }
    if (!ended) {
      // The session cookie may still be valid: showing "signed out" here would
      // leave the next person on a shared device inside this account.
      return false;
    }
    latestRequest.current += 1; // any /auth/me still in flight is now stale
    clearReturnTo();
    setState({ status: 'signed-out', deliberate: true });
    return true;
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      saveReturnTo(currentPath());
      setState({ status: 'signed-out' });
    });
    void refresh();
    return () => setUnauthorizedHandler(null);
  }, [refresh]);

  const value = useMemo(
    () => ({ state, refresh, signOut }),
    [state, refresh, signOut]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** The auth state; throws outside an AuthProvider. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return value;
}

/** The auth state, or null outside an AuthProvider (for shared components). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
